/**
 * LensErrorBoundary — the mandatory render guard for the Leads "Lenses"
 * pilot (AD-046 §13, project memory: AVBP tests are text-assert → green ≠
 * renders; no error boundary = white screen).
 *
 * A single Lens view crashing must NEVER white-screen the whole portal — the
 * Existing tab must always stay reachable. Each new view (and the switcher
 * shell) is wrapped in this boundary; on a crash it shows a compact inline
 * fallback that NAMES the failing view and offers a reset, instead of
 * unmounting the entire route. Mirrors the L1A ProjectErrorBoundary pattern.
 */
import React from 'react';

interface Props {
  /** Human label of the wrapped surface (named in the fallback). */
  label: string;
  children: React.ReactNode;
  /** Optional reset hook (e.g. switch back to Existing). */
  onReset?: () => void;
}
interface State {
  error: Error | null;
}

export default class LensErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface the failing component on screen + in the console (text-assert
    // tests can't catch a render crash; this makes it loud).
    // eslint-disable-next-line no-console
    console.error(`[LensErrorBoundary] ${this.props.label} crashed:`, error, info);
  }

  private reset = (): void => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div
          role="alert"
          style={{
            margin: 16,
            padding: '14px 16px',
            border: '1px solid var(--crit, #DC2626)',
            background: 'var(--crit-wash, #FEF2F2)',
            borderRadius: 'var(--r-md, 13px)',
            color: 'var(--ink, #1A1A1A)',
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            The “{this.props.label}” view hit an error
          </div>
          <div style={{ color: 'var(--ink-soft, #4B5563)', marginBottom: 8 }}>
            The rest of the portal is fine — switch to another view or reset
            this one. (Details in the console.)
          </div>
          <code
            style={{
              display: 'block',
              fontSize: 11,
              color: 'var(--crit, #DC2626)',
              marginBottom: 8,
              wordBreak: 'break-word',
            }}
          >
            {this.state.error.message}
          </code>
          <button
            type="button"
            onClick={this.reset}
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: '5px 12px',
              borderRadius: 'var(--r-pill, 999px)',
              border: '1px solid var(--rule-strong, #D1D5DB)',
              background: 'var(--paper, #fff)',
              cursor: 'pointer',
            }}
          >
            Reset view
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
